/**
 * useEchoFilter Hook
 * Handles filtering of system TTS echo from user speech recognition
 * 
 * The microphone often picks up the system's TTS output, creating "echo".
 * This hook provides utilities to detect and remove this echo from transcripts.
 * 
 * Restored from feat/symptom-checker branch
 */

import { useRef, useCallback } from 'react';

/**
 * Normalize text for comparison
 * IMPORTANT: Replace hyphens with spaces because speech recognition often breaks hyphenated words
 * e.g., "pressure-like" in text becomes "pressure like" in speech recognition
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
/**
 * Normalize medication/dosage terms so TTS text and STT transcript compare equal
 * (e.g. "25 mg" vs "25 milligrams", "qd" vs "once daily")
 */
function normalizeMedicationTerms(s) {
  return s
    .replace(/\bmg\b/g, 'milligrams')
    .replace(/\bmilligrams\b/g, 'milligrams')
    .replace(/\bqd\b/g, 'once daily')
    .replace(/\bbid\b/g, 'twice daily')
    .replace(/\btid\b/g, 'three times daily')
    .replace(/\bonce daily\b/g, 'once daily')
    .replace(/\btwice daily\b/g, 'twice daily')
    .replace(/\bthree times daily\b/g, 'three times daily');
}

function normalize(text) {
  const base = text.toLowerCase()
    .replace(/-/g, ' ')                    // Replace hyphens with spaces
    .replace(/[?.,!'"()]/g, '')             // Remove punctuation including parens so "(STI)" matches "sti"
    .replace(/\s+/g, ' ')                   // Collapse multiple spaces
    .trim();
  return normalizeMedicationTerms(base);
}

/**
 * Strip system echo from user speech
 * @param {string} speech - The raw speech transcript
 * @param {string[]} systemUtterances - Array of recent system utterances to filter out
 * @returns {string} - The cleaned speech with system echo removed
 */
/**
 * Collapse consecutive duplicate words: "based on your based on your medical" -> "based on your medical".
 * STT incremental chunks often produce this pattern when echoing TTS.
 */
function collapseConsecutiveDupeWords(words) {
  const out = [];
  for (let i = 0; i < words.length; i++) {
    if (i === 0 || words[i] !== words[i - 1]) out.push(words[i]);
  }
  return out;
}

/**
 * Detect "repetitive prefix" echo: STT sends incremental chunks that get concatenated
 * e.g. "based on your" + "based on your medical" -> "based on your based on your medical".
 * After collapsing consecutive duplicate words, if the result is a prefix of the utterance, it's echo.
 */
function isRepetitivePrefixEcho(normalizedSpeech, normalizedUtterance) {
  if (!normalizedSpeech || !normalizedUtterance || normalizedUtterance.length < 15) return false;
  const speechWords = normalizedSpeech.split(/\s+/).filter(w => w.length > 0);
  const utteranceWords = normalizedUtterance.split(/\s+/).filter(w => w.length > 0);
  if (speechWords.length < 4) return false;
  const collapsed = collapseConsecutiveDupeWords(speechWords);
  if (collapsed.length < 3) return false;
  const collapsedStr = collapsed.join(' ');
  const utterancePrefix = utteranceWords.slice(0, Math.min(utteranceWords.length, collapsed.length + 5)).join(' ');
  const isPrefixOfUtterance = utterancePrefix.startsWith(collapsedStr) || collapsedStr.startsWith(utteranceWords.slice(0, 8).join(' '));
  const wasRepetitive = speechWords.length > collapsed.length * 1.2;
  return wasRepetitive && isPrefixOfUtterance;
}

/**
 * Check if speech is mostly an ordered subset of the utterance (transcript echoes the question).
 * Catches full echo and partial echo where user said nothing.
 * Threshold 0.75: only treat as echo when 75%+ of user words match question in order (was 0.6)
 * to reduce false discards when user's phrasing overlaps slightly with the question.
 */
function isMostlyOrderedSubsetOf(normalizedSpeech, normalizedUtterance) {
  const speechWords = normalizedSpeech.split(/\s+/).filter(w => w.length > 0);
  const utteranceWords = normalizedUtterance.split(/\s+/).filter(w => w.length > 0);
  if (speechWords.length < 3 || utteranceWords.length < 5) return false;
  let uIdx = 0;
  let matchCount = 0;
  for (const w of speechWords) {
    while (uIdx < utteranceWords.length && utteranceWords[uIdx] !== w) uIdx++;
    if (uIdx < utteranceWords.length) {
      matchCount++;
      uIdx++;
    }
  }
  return matchCount / speechWords.length >= 0.75;
}

/**
 * Minimal safe confirmations: ONLY phrases that cannot be the tail of a system question.
 * We never allow "correct", "that's correct", "right" etc. because TTS often ends with "is that correct?".
 * So only single-word (or clearly user-only) yes/no style.
 */
const MINIMAL_SAFE_CONFIRMATIONS = new Set([
  'yes', 'yeah', 'yep', 'yup', 'no', 'nope', 'sure', 'ok', 'okay', 'uh huh', 'mhm',
]);

/**
 * True only if speech is exclusively a minimal safe confirmation (e.g. "yes", "no").
 * Used to override echo discard only when we're certain it's not TTS (TTS never says just "yes").
 */
function isMinimalSafeConfirmation(normalizedSpeech) {
  if (!normalizedSpeech || normalizedSpeech.length > 30) return false;
  const t = normalizedSpeech.trim();
  if (MINIMAL_SAFE_CONFIRMATIONS.has(t)) return true;
  // "yes it is", "no it isnt" etc. - must start with yes/no and be short
  const words = t.split(/\s+/).filter(w => w.length > 0);
  if (words.length >= 2 && words.length <= 4 && ['yes', 'yeah', 'no', 'nope'].includes(words[0])) {
    return true;
  }
  return false;
}

/**
 * Returns true if speech is clearly the system utterance (full or large overlap).
 * TTS must NEVER be accepted as user input.
 */
function isClearlySystemEcho(normalizedSpeech, normalizedUtterance) {
  if (!normalizedSpeech || !normalizedUtterance) return false;
  // Exact or near-exact match
  if (normalizedSpeech === normalizedUtterance) return true;
  if (normalizedUtterance.includes(normalizedSpeech) && normalizedSpeech.length >= 15) return true;
  if (normalizedSpeech.includes(normalizedUtterance) && normalizedUtterance.length >= 15) return true;
  // Speech is long and mostly matches utterance (ordered subset with high ratio)
  const speechWords = normalizedSpeech.split(/\s+/).filter(w => w.length > 0);
  const utteranceWords = normalizedUtterance.split(/\s+/).filter(w => w.length > 0);
  if (speechWords.length >= 5 && utteranceWords.length >= 5) {
    let uIdx = 0;
    let matchCount = 0;
    for (const w of speechWords) {
      while (uIdx < utteranceWords.length && utteranceWords[uIdx] !== w) uIdx++;
      if (uIdx < utteranceWords.length) {
        matchCount++;
        uIdx++;
      }
    }
    if (matchCount / speechWords.length >= 0.7) return true;
  }
  return false;
}

export function stripSystemEcho(speech, systemUtterances) {
  if (!speech || !systemUtterances || systemUtterances.length === 0) {
    return speech;
  }
  
  let cleaned = speech;
  const normalizedCleaned = normalize(cleaned);
  const speechWords = normalizedCleaned.split(/\s+/).filter((w) => w.length > 0);
  const speechWordCount = speechWords.length;

  // Short responses (1-4 words): only discard on exact match to a system utterance.
  // Avoids stripping or discarding real short answers (e.g. "three days", "in my chest", "yes sometimes").
  if (speechWordCount <= 4) {
    for (const utterance of systemUtterances) {
      if (!utterance) continue;
      if (normalize(utterance) === normalizedCleaned) return '';
    }
    return speech;
  }

  // --- STEP 1: Never accept clear TTS echo. No exceptions. ---
  const lastUtterance = systemUtterances[systemUtterances.length - 1];
  if (lastUtterance) {
    const normalizedLast = normalize(lastUtterance);
    if (normalizedLast.length >= 10) {
      // Review prompt echo: prompts ending with "keep, remove, or edit are your choices" (or legacy "Say keep, remove, or edit.")
      // When STT picks up the echo (possibly with a misheard drug name) and the user appends their answer,
      // the transcript is long but ends with the user's real intent word (keep/remove/edit).
      // The prompt itself ends with "your choices" (new) or "edit" (legacy), never with "keep" or "remove".
      const isReviewPrompt = normalizedLast.length >= 15 && normalizedLast.includes('keep') && normalizedLast.includes('remove') && normalizedLast.includes('edit');
      if (isReviewPrompt && speechWordCount >= 5 && normalizedCleaned.length >= 10) {
        const intentMatch = normalizedCleaned.match(/\b(keep|remove|edit)\s*\.?$/i);
        if (intentMatch) {
          const intent = intentMatch[1].toLowerCase();
          // The prompt ends with "your choices" or "edit". If trailing word is the same as the prompt's last word,
          // it's likely pure echo. Only extract when trailing word differs (user appended their real answer).
          const lastUtteranceWords = normalizedLast.split(/\s+/).filter(w => w.length > 0);
          const promptLastWord = lastUtteranceWords[lastUtteranceWords.length - 1];
          if (intent !== promptLastWord) {
            return intent;
          }
          // Trailing word matches prompt ending — pure echo. Discard entirely.
          return '';
        }
      }
      // Only run aggressive echo checks when speech is 6+ words; 5-word answers (e.g. "about three days now") must not be discarded as echo.
      if (speechWordCount >= 6) {
        if (isClearlySystemEcho(normalizedCleaned, normalizedLast)) {
          const trailing1 = speechWords.length >= 1 ? speechWords.slice(-1).join(' ') : '';
          const trailing2 = speechWords.length >= 2 ? speechWords.slice(-2).join(' ') : '';
          // Review prompt echo: keep trailing intent (keep/remove/edit) when it's a review-style prompt
          const isReviewEcho = normalizedLast.includes('keep') && normalizedLast.includes('remove') && normalizedLast.includes('edit');
          if (isReviewEcho && ['keep', 'remove', 'edit'].includes(trailing1)) {
            return trailing1;
          }
          // Section prompt echo -> keep trailing section name
          const validSectionTails = ['name', 'dosage', 'frequency', 'pharmacy', 'notes', 'prescribed by'];
          if (validSectionTails.includes(trailing1)) {
            return trailing1;
          }
          if (trailing2 === 'prescribed by' || validSectionTails.includes(trailing2)) {
            return trailing2;
          }
          return '';
        }
        if (isRepetitivePrefixEcho(normalizedCleaned, normalizedLast)) {
          return '';
        }
        if (isMostlyOrderedSubsetOf(normalizedCleaned, normalizedLast)) {
          // Only allow through if it's unambiguously a minimal safe confirmation (yes/no), never "correct" etc.
          if (isMinimalSafeConfirmation(normalizedCleaned)) {
            return speech;
          }
          return '';
        }
      }
      // Speech is tail of last utterance (e.g. "or say no to finish" after ADD_PROMPT).
      // Only run for 6+ word speech so short answers (e.g. "about a week", "in my chest") are never discarded here.
      const lastWords = normalizedLast.split(/\s+/).filter((w) => w.length > 0);
      if (lastWords.length >= 4 && speechWords.length >= 6) {
        for (let n = 4; n <= Math.min(6, lastWords.length, speechWords.length); n++) {
          const utteranceTail = lastWords.slice(-n).join(' ');
          const speechTail = speechWords.slice(-n).join(' ');
          if (utteranceTail.length >= 10 && speechTail === utteranceTail) {
            return '';
          }
        }
      }
    }
  }

  // Check against all recent utterances for clear echo (e.g. STT delayed and matched an older question).
  // Only discard when user's speech is long enough (6+ words) to be likely echo; short answers must pass.
  const cleanedWordCount = normalizedCleaned.split(/\s+/).filter((w) => w.length > 0).length;
  if (cleanedWordCount >= 6) {
    for (const utterance of systemUtterances) {
      if (!utterance) continue;
      const normalizedUtterance = normalize(utterance);
      if (normalizedUtterance.length >= 15 && isClearlySystemEcho(normalizedCleaned, normalizedUtterance)) {
        return '';
      }
    }
  }
  
  for (const utterance of systemUtterances) {
    if (!utterance) continue;
    
    const normalizedUtterance = normalize(utterance);
    const normalizedCleaned = normalize(cleaned);
    
    // Skip if utterance is too short (might match valid responses)
    if (normalizedUtterance.length < 10) continue;
    
    // Case 1: Speech exactly matches system utterance (pure echo)
    if (normalizedCleaned === normalizedUtterance) {
      return '';
    }
    
    // Case 2: Speech STARTS with system utterance (TTS audio captured before user speech)
    // Only for 6+ word speech so short answers are never stripped (reduces "having to repeat myself")
    if (speechWordCount >= 6 && normalizedCleaned.startsWith(normalizedUtterance.substring(0, 30))) {
      const utteranceWords = normalizedUtterance.split(' ');
      const cleanedWords = cleaned.split(/\s+/);
      const cleanedWordsNormalized = cleanedWords.map(w => normalize(w));

      let matchingWords = 0;
      for (let i = 0; i < Math.min(utteranceWords.length, cleanedWordsNormalized.length); i++) {
        if (cleanedWordsNormalized[i] === utteranceWords[i]) {
          matchingWords++;
        } else {
          break;
        }
      }

      if (matchingWords >= 4) {
        const remaining = cleanedWords.slice(matchingWords).join(' ').trim();
        if (remaining.length > 0) {
          cleaned = remaining;
          continue;
        } else {
          return '';
        }
      }
    }
    
    // Case 3: Speech contains system utterance somewhere in the middle
    const utteranceIndex = normalizedCleaned.indexOf(normalizedUtterance);
    if (utteranceIndex !== -1) {
      const utteranceWords = normalizedUtterance.split(' ');
      const cleanedWords = cleaned.split(/\s+/);
      const cleanedWordsNormalized = cleanedWords.map(w => normalize(w));
      
      // Find where the utterance starts in the word array
      let startWordIndex = -1;
      for (let i = 0; i <= cleanedWordsNormalized.length - utteranceWords.length; i++) {
        let matches = true;
        for (let j = 0; j < utteranceWords.length; j++) {
          if (cleanedWordsNormalized[i + j] !== utteranceWords[j]) {
            matches = false;
            break;
          }
        }
        if (matches) {
          startWordIndex = i;
          break;
        }
      }
      
      if (startWordIndex !== -1) {
        // Found the echo - extract words before and after
        const beforeWords = cleanedWords.slice(0, startWordIndex);
        const afterWords = cleanedWords.slice(startWordIndex + utteranceWords.length);
        const extractedSpeech = [...beforeWords, ...afterWords].join(' ').trim();
        
        if (extractedSpeech.length > 0) {
          cleaned = extractedSpeech;
        } else {
          return '';
        }
      }
    }
    
    // Case 4: Check for partial question echo (first few words of question)
    // Only for 6+ word speech so short answers (e.g. "about a week", "yes sometimes") are never stripped here
    if (speechWordCount >= 6) {
      const firstFewWords = normalizedUtterance.split(' ').slice(0, 6).join(' ');
      if (firstFewWords.length > 10 && normalizedCleaned.startsWith(firstFewWords)) {
        const cleanedWords = cleaned.split(/\s+/);
        const fewWords = firstFewWords.split(' ').length;
        const remaining = cleanedWords.slice(fewWords).join(' ').trim();
        if (remaining.length > 0) {
          cleaned = remaining;
        }
      }
    }

    // Case 5: Mic picked up MIDDLE/END of system utterance (missed the beginning)
    // Only for 6+ word speech so short answers are never stripped (reduces "having to repeat myself")
    const utteranceWords = normalizedUtterance.split(' ');
    const cleanedWords = normalizedCleaned.split(' ');

    if (speechWordCount >= 6 && utteranceWords.length >= 4 && cleanedWords.length >= 2) {
      // Look for the start of user's speech in the system utterance (after position 0)
      for (let startPos = 1; startPos < utteranceWords.length - 2; startPos++) {
        // Check if user speech starts with words from position startPos in system utterance
        let matchCount = 0;
        const maxCheck = Math.min(utteranceWords.length - startPos, cleanedWords.length);
        
        for (let i = 0; i < maxCheck; i++) {
          if (cleanedWords[i] === utteranceWords[startPos + i]) {
            matchCount++;
          } else {
            break;
          }
        }
        
        // If we matched 3+ consecutive words from middle of system utterance
        if (matchCount >= 3) {
          // These matched words are likely echo - extract what comes after
          const originalCleanedWords = cleaned.split(/\s+/).filter((w) => w.length > 0);
          const remaining = originalCleanedWords.slice(matchCount).join(' ').trim();
          
          if (remaining.length > 0) {
            cleaned = remaining;
            break; // Found and removed the echo
          } else {
            // Only echo, no remaining user speech - but short transcripts (e.g. 3–5 words) may be
            // partial answers or STT splitting; don't silence the user, pass through.
            if (originalCleanedWords.length >= 6) {
              return '';
            }
            // Short overlap (e.g. "the frequency be"): likely partial capture, let it through
            break;
          }
        }
      }
    }
  }
  
  return cleaned;
}

/**
 * Check if transcript is a potential echo of the last question
 * @param {string} transcript - Speech transcript
 * @param {string} lastQuestion - Last question asked by system
 * @returns {boolean} - True if transcript appears to be an echo
 */
export function isPotentialEcho(transcript, lastQuestion) {
  if (!transcript || !lastQuestion) return false;
  
  const normalizedTranscript = normalize(transcript);
  const normalizedQuestion = normalize(lastQuestion);
  
  // Exact match
  if (normalizedTranscript === normalizedQuestion) {
    return true;
  }
  
  // Transcript contains most of the question (>70% overlap)
  const questionWords = normalizedQuestion.split(' ');
  const transcriptWords = normalizedTranscript.split(' ');
  
  if (questionWords.length > 5) {
    const overlap = questionWords.filter(w => transcriptWords.includes(w)).length;
    const overlapRatio = overlap / questionWords.length;
    if (overlapRatio > 0.7) {
      return true;
    }
  }
  
  return false;
}

/**
 * Custom hook for echo filtering
 * Manages the list of system utterances and provides filtering functions
 */
export function useEchoFilter() {
  // Track recent system utterances for echo filtering
  const systemUtterancesRef = useRef([]);
  const lastSpokenQuestionRef = useRef('');
  
  // Cooldown tracking - don't accept input immediately after TTS
  const echoCooldownRef = useRef(false);
  const echoCooldownTimeoutRef = useRef(null);
  
  /**
   * Add a system utterance to the echo filter list
   * @param {string} utterance - Text that the system spoke
   */
  const addSystemUtterance = useCallback((utterance) => {
    if (!utterance) return;
    
    systemUtterancesRef.current.push(utterance);
    lastSpokenQuestionRef.current = utterance;
    
    // Keep only last 5 utterances
    if (systemUtterancesRef.current.length > 5) {
      systemUtterancesRef.current.shift();
    }
    
  }, []);
  
  /**
   * Clear all system utterances (on conversation restart)
   */
  const clearSystemUtterances = useCallback(() => {
    systemUtterancesRef.current = [];
    lastSpokenQuestionRef.current = '';
  }, []);
  
  /**
   * Start echo cooldown (call when TTS starts)
   */
  const startEchoCooldown = useCallback(() => {
    echoCooldownRef.current = true;
  }, []);
  
  /**
   * End echo cooldown after delay (call when TTS ends)
   * @param {number} delayMs - Delay before ending cooldown
   * @param {function} [onEndCallback] - Optional callback when cooldown ends (e.g. to re-enable input UI)
   */
  const endEchoCooldown = useCallback((delayMs = 500, onEndCallback) => {
    if (echoCooldownTimeoutRef.current) {
      clearTimeout(echoCooldownTimeoutRef.current);
    }
    
    echoCooldownTimeoutRef.current = setTimeout(() => {
      echoCooldownRef.current = false;
      echoCooldownTimeoutRef.current = null;
      onEndCallback?.();
    }, delayMs);
  }, []);
  
  /**
   * Immediately end echo cooldown (e.g. user barge-in during TTS).
   */
  const clearEchoCooldown = useCallback(() => {
    if (echoCooldownTimeoutRef.current) {
      clearTimeout(echoCooldownTimeoutRef.current);
      echoCooldownTimeoutRef.current = null;
    }
    echoCooldownRef.current = false;
  }, []);

  /**
   * Check if we're in echo cooldown period
   * @returns {boolean} - True if in cooldown
   */
  const isInCooldown = useCallback(() => {
    return echoCooldownRef.current;
  }, []);
  
  /**
   * Filter echo from speech transcript
   * @param {string} speech - Raw speech transcript
   * @returns {string} - Cleaned speech
   */
  const filterEcho = useCallback((speech) => {
    return stripSystemEcho(speech, systemUtterancesRef.current);
  }, []);
  
  /**
   * Check if speech is a pure echo
   * @param {string} speech - Speech transcript
   * @returns {boolean} - True if speech is just echo
   */
  const isEcho = useCallback((speech) => {
    return isPotentialEcho(speech, lastSpokenQuestionRef.current);
  }, []);
  
  return {
    addSystemUtterance,
    clearSystemUtterances,
    startEchoCooldown,
    endEchoCooldown,
    clearEchoCooldown,
    isInCooldown,
    filterEcho,
    isEcho,
    getLastSpokenQuestion: () => lastSpokenQuestionRef.current,
  };
}

export default useEchoFilter;
